/**
 * Super Admin — Reviews Moderation Queue.
 *
 * Surfaces every review currently `pending_review`, `hidden`, or `rejected`
 * with approve / reject / ban-author actions. Wires to
 * /api/admin/community/queue (see backend/routers/reviews.py).
 */
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ShieldAlert, CheckCircle2, Ban, XCircle, Star, Flag, Loader2, Filter,
} from "lucide-react";
import { toast } from "sonner";

type Status = "pending_review" | "hidden" | "rejected" | "approved" | "all";

interface Review {
  review_id: string;
  product_id: string;
  product_title?: string | null;
  product_slug?: string | null;
  author_user_id: string;
  author_name: string;
  rating: number;
  title?: string | null;
  body: string;
  verified_purchase: boolean;
  upvotes: number;
  downvotes: number;
  flagged: boolean;
  flag_reasons: Array<{ by: string; reason: string; at: string }>;
  moderation_status: string;
  created_at: string;
  updated_at: string;
}

interface QueueResp { items: Review[]; total: number; has_more: boolean }
interface StatsResp { pending: number; hidden: number; rejected: number; approved: number; flagged_total: number }

const STATUS_LABEL: Record<Status, string> = {
  pending_review: "Pending",
  hidden: "Hidden",
  rejected: "Rejected",
  approved: "Approved",
  all: "All flagged",
};

export default function SuperAdminModeration() {
  const [status, setStatus] = React.useState<Status>("pending_review");
  const qc = useQueryClient();

  const stats = useQuery<StatsResp>({
    queryKey: ["mod-stats"],
    queryFn: async () => (await api.get("/admin/community/queue/stats")).data,
  });

  const queue = useQuery<QueueResp>({
    queryKey: ["mod-queue", status],
    queryFn: async () => (await api.get(`/admin/community/queue?status=${status}&limit=100`)).data,
  });

  const act = useMutation({
    mutationFn: async ({ review_id, action, reason }: { review_id: string; action: "approve" | "reject" | "ban-author"; reason?: string }) => {
      const r = await api.post(`/admin/community/reviews/${review_id}/${action}`, { reason });
      return r.data;
    },
    onSuccess: (_data, vars) => {
      toast.success(
        vars.action === "approve" ? "Approved — review is live."
        : vars.action === "reject" ? "Rejected — review hidden from PDP."
        : "Author banned + all their reviews rejected."
      );
      qc.invalidateQueries({ queryKey: ["mod-queue"] });
      qc.invalidateQueries({ queryKey: ["mod-stats"] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || "Action failed");
    },
  });

  return (
    <DashboardLayout
      title="Reviews Moderation"
      subtitle="Approve, reject, or ban authors. Every action recomputes SibaScore for the affected product."
      seoNoindex
    >
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="mod-stats-strip">
        {(["pending", "hidden", "rejected", "approved", "flagged_total"] as const).map((k) => {
          const label = { pending: "Pending", hidden: "Hidden", rejected: "Rejected", approved: "Approved", flagged_total: "Flagged (all)" }[k];
          const tone  = { pending: "amber", hidden: "slate", rejected: "rose", approved: "emerald", flagged_total: "fuchsia" }[k];
          const value = stats.data?.[k] ?? 0;
          return (
            <div key={k} data-testid={`mod-stat-${k}`}
                 className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-4">
              <div className={`text-[10px] uppercase tracking-[0.24em] font-semibold text-${tone}-700 dark:text-${tone}-300`}>{label}</div>
              <div className="font-serif-display text-3xl font-bold text-[color:var(--sg-fg)] mt-1 tabular-nums">{value}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="mod-filters">
        <Filter className="h-3.5 w-3.5 text-[color:var(--sg-muted)]"/>
        {(Object.keys(STATUS_LABEL) as Status[]).map((s) => (
          <button
            key={s}
            data-testid={`mod-filter-${s}`}
            onClick={() => setStatus(s)}
            className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${
              status === s
                ? "bg-[color:var(--sb-navy,#0A1128)] text-white border-transparent"
                : "border-[color:var(--sg-border)] text-[color:var(--sg-muted)] hover:bg-[color:var(--sg-muted-bg)]"
            }`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {/* Queue */}
      {queue.isLoading ? (
        <div className="flex items-center justify-center py-16 text-[color:var(--sg-muted)]" data-testid="mod-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2"/> Loading queue…
        </div>
      ) : (queue.data?.items.length ?? 0) === 0 ? (
        <div className="rounded-2xl border border-dashed border-[color:var(--sg-border)] p-10 text-center text-[color:var(--sg-muted)]" data-testid="mod-empty">
          <ShieldAlert className="h-8 w-8 mx-auto mb-2 text-emerald-500"/>
          Nothing to moderate in this bucket. Great work.
        </div>
      ) : (
        <div className="space-y-3" data-testid="mod-list">
          {queue.data!.items.map((r) => (
            <ReviewCard key={r.review_id} review={r}
              busy={act.isPending && act.variables?.review_id === r.review_id}
              action={act.variables?.action}
              onAction={(action) => {
                const reason = action === "ban-author"
                  ? window.prompt("Reason for banning this author?")?.trim() || "banned by super admin"
                  : undefined;
                act.mutate({ review_id: r.review_id, action, reason });
              }}
            />
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}

function ReviewCard({
  review, busy, action, onAction,
}: {
  review: Review;
  busy: boolean;
  action?: "approve" | "reject" | "ban-author";
  onAction: (a: "approve" | "reject" | "ban-author") => void;
}) {
  const statusColor: Record<string, string> = {
    pending_review: "amber", hidden: "slate", rejected: "rose", approved: "emerald",
  };
  const tone = statusColor[review.moderation_status] || "slate";
  return (
    <div data-testid={`mod-card-${review.review_id}`} className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-${tone}-100 text-${tone}-800 dark:bg-${tone}-900/40 dark:text-${tone}-200`}>
              {review.moderation_status.replace("_", " ")}
            </span>
            {review.flagged && (
              <span className="text-[10px] font-bold text-rose-700 dark:text-rose-300 flex items-center gap-1">
                <Flag className="h-3 w-3"/> Flagged
              </span>
            )}
            {review.verified_purchase && (
              <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3"/> Verified purchase
              </span>
            )}
            <div className="text-[10px] text-[color:var(--sg-muted)] ml-auto">
              {new Date(review.created_at).toLocaleString()}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`h-3.5 w-3.5 ${i < review.rating ? "fill-amber-400 text-amber-400" : "text-slate-300 dark:text-slate-600"}`}/>
              ))}
            </div>
            <span className="font-serif-display text-lg font-bold text-[color:var(--sg-fg)] truncate">{review.title || "(no title)"}</span>
          </div>
          <p className="mt-2 text-sm text-[color:var(--sg-fg)] leading-relaxed">{review.body}</p>
          <div className="mt-2 text-[11px] text-[color:var(--sg-muted)]">
            By <b className="text-[color:var(--sg-fg)]">{review.author_name}</b> · {review.upvotes} up · {review.downvotes} down
            {" · "}
            Product: {review.product_slug ? (
              <a className="underline hover:text-[color:var(--sb-saffron,#F97316)]"
                 href={`/shop/p/${review.product_slug}`} target="_blank" rel="noreferrer">
                {review.product_title || review.product_id}
              </a>
            ) : (review.product_title || review.product_id)}
          </div>
          {review.flag_reasons?.length > 0 && (
            <div className="mt-2 rounded-lg border border-rose-300/40 bg-rose-500/5 px-3 py-2 text-[11px] text-rose-800 dark:text-rose-200">
              <div className="font-semibold">Why it was flagged</div>
              <ul className="mt-1 space-y-0.5">
                {review.flag_reasons.slice(0, 3).map((f, i) => (
                  <li key={i}>· {f.reason} <span className="opacity-60">({f.by})</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          data-testid={`mod-approve-${review.review_id}`}
          size="sm"
          className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
          disabled={busy}
          onClick={() => onAction("approve")}
        >
          {busy && action === "approve" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <CheckCircle2 className="h-3.5 w-3.5 mr-1"/>} Approve
        </Button>
        <Button
          data-testid={`mod-reject-${review.review_id}`}
          size="sm"
          variant="outline"
          className="h-8 border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-900/30"
          disabled={busy}
          onClick={() => onAction("reject")}
        >
          {busy && action === "reject" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <XCircle className="h-3.5 w-3.5 mr-1"/>} Reject
        </Button>
        <Button
          data-testid={`mod-ban-${review.review_id}`}
          size="sm"
          variant="outline"
          className="h-8 border-slate-400 text-slate-800 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900/40"
          disabled={busy}
          onClick={() => onAction("ban-author")}
        >
          {busy && action === "ban-author" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <Ban className="h-3.5 w-3.5 mr-1"/>} Ban author
        </Button>
      </div>
    </div>
  );
}
